import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-br from-primary via-primary/95 to-primary/85 text-primary-foreground hover:from-primary/100 hover:via-primary/98 hover:to-primary/90 shadow-[0_2px_8px_rgba(0,0,0,0.1),0_0_16px_hsl(var(--primary)/0.3)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.15),0_0_24px_hsl(var(--primary)/0.4)] active:scale-[0.98]",
        destructive:
          "bg-gradient-to-br from-destructive via-destructive/90 to-destructive/80 text-white hover:from-destructive/100 hover:via-destructive/95 hover:to-destructive/90 focus-visible:ring-destructive/40 shadow-[0_2px_8px_rgba(0,0,0,0.1),0_0_16px_hsl(var(--destructive)/0.3)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.15),0_0_24px_hsl(var(--destructive)/0.4)] active:scale-[0.98]",
        outline:
          "border-2 bg-gradient-to-br from-background/80 via-background/60 to-background/40 backdrop-blur-sm border-input/60 shadow-[0_1px_3px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,0.1)] hover:from-background/90 hover:via-background/70 hover:to-background/50 hover:border-input hover:text-accent-foreground hover:shadow-[0_2px_8px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.15)] active:scale-[0.98]",
        secondary:
          "bg-gradient-to-br from-secondary via-secondary/95 to-secondary/85 text-secondary-foreground hover:from-secondary/100 hover:via-secondary/98 hover:to-secondary/90 shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] active:scale-[0.98]",
        ghost:
          "hover:bg-gradient-to-br hover:from-accent/70 hover:via-accent/60 hover:to-accent/50 hover:text-accent-foreground hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80 transition-colors",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
